<script lang="ts" setup>
import logoUrl from '@/assets/biene.svg';
import config from '@/config';
import { useAuthStore } from '@/stores/authStore';
import { useRoleCreateStore } from '@/stores/role-create-store';
import {
  BusinessParametersPaths,
  EdiErrorPath,
  FileTransferErrorPath,
  LoggingsPaths,
  ManualCallsPaths,
  NewsAdministrationPaths,
  PermissionsPaths,
  ReportsPaths,
  RolesPaths,
  SchedulerPaths,
  DispoImportPath,
  TechicalParametersPaths,
} from '@/utils/constants/system-constants';
import { determineFunctionVisibility, PermissionFunction } from '@/utils/permissions/permissions-utils';
import { RoleModel } from '@/views/system/roles/role-model';
import type { NavItemDefinition } from '@scu/core-ui/dist/types/components/scu-top-bar/types';
import { DE, GB } from '@scu/flags';
import {
  bell,
  calendar_clock,
  circle_attention,
  computer_settings,
  key_settings,
  login,
  logout,
  newspaper,
  profile,
  send,
  setting_search,
  triangle_warning,
  user_check,
  user_group,
  file_x,
  upload,
} from '@scu/icons';
import {
  ScuAvatar,
  ScuButton,
  ScuChip,
  ScuHeadline,
  ScuIcon,
  ScuLink,
  ScuModalDialogue,
  ScuTopBar,
  ScuTopBarContextDropdown,
  ScuTopBarUser,
} from '@scu/vue';
import { computed, onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { AppInfoModel, useInfoStore, VersionInfoModel } from '../../stores/app-info-store';
import { formatDateToGermanDateTimeString } from '../../utils/formatter/date-formatter';

const props = defineProps<{
  /** Currently selected application locale/language. */
  locale: keyof typeof config.i18n.locales;
  /** Nav items to display. */
  navItems?: NavItemDefinition[];
  /**
   * Username of the currently logged in user.
   * If unset, the user will be treated as logged out which displays a login action (instead of logout) and hides the user information in the fly out.
   */
  username?: string;
}>();

const emit = defineEmits<{
  /** triggered when a different application locale/language is selected. */
  'update:locale': [value: string];
  /** triggered when the back button is clicked. */
  backButtonClick: [];
  /** triggered when a navigation should be done (either by clicking a nav item or the app logo/name). */
  navigate: [route: string];
  /** triggered when the user is logged out and clicks the login action. */
  login: [];
  /** triggered when the user is logged in and clicks the logout action. */
  logout: [];
}>();

const { t } = useI18n();

const { navItems } = toRefs(props);
const authStore = useAuthStore();
const infoStore = useInfoStore();
const rolesStore = useRoleCreateStore();
const appInfo = ref({} as AppInfoModel);
const allRolesInStage = ref([] as RoleModel[]);
const currentStage = Object.create(globalThis.import_meta_env || null).ODJ_EE_STAGE.trim().toUpperCase();
const stage = currentStage === 'PROD' ? '' : currentStage + ' STAGE';

const isModalDialogOpen = ref(false);
const openModalInfo = (isOpen: boolean) => (isModalDialogOpen.value = isOpen);

/** Gets the formatted label for the current locale. */
const localeLabel = computed(() => {
  const [language, region = ''] = props.locale.toUpperCase().split('-');
  if (!region || language === region) return language;

  const hasOtherRegions =
    Object.keys(config.i18n.locales).filter((locale) => locale.toUpperCase().startsWith(language)).length > 1;

  return hasOtherRegions ? `${language} (${region})` : language;
});

const getInfoText = (versionInfo: VersionInfoModel): string => {
  let text = `${versionInfo.componentName}: `;
  text += versionInfo.componentVersion;
  text += versionInfo.componentBuildDate ? ` ${t('pages.system.systemInfo.from')} ` : '';
  text += versionInfo.componentBuildDate
    ? formatDateToGermanDateTimeString(new Date(versionInfo.componentBuildDate))
    : '';

  return text;
};

onMounted(() => {
  (async () => {
    appInfo.value = (await infoStore.fetchAppInfo()).value;
    allRolesInStage.value = (await rolesStore.getAllRoles()).value;
  })();
});

const handleLoginButtonClick = () => {
  emit('login');
  emit('navigate', '/');
};

/**
 * returns a newline-separated string of short role descriptions (roleShort)
 * for all user roles that are relevant in the current stage
 *
 * - compares them userRoles against the list of roles available in the current stage
 * - maps matching SIAM keys to their corresponding roleShort labels
 *
 * @param userRoles - a string, array of strings, or undefined. Each entry is expected
 *                    to contain a SIAM key
 *
 * @returns a string with all matching roleShort values, separated by newline characters (`\n`)
 *          if no matches are found, an empty string is returned
 */
const getUserRolesShortDescription = (userRoles: string | string[] | undefined): string => {
  if (!userRoles) return '';

  let shortDesc = '';
  const allStageRoles = allRolesInStage.value;
  const allStageSiamKeys = allStageRoles.map((role) => role.siamKey);

  const getRoleShort = (siamKey: string): string =>
    allStageRoles.find((role) => role.siamKey === siamKey)?.roleShort ?? '';

  if (Array.isArray(userRoles)) {
    shortDesc = userRoles
      .filter((siamKey) => allStageSiamKeys.includes(siamKey))
      .map(getRoleShort)
      .join('\n');
  } else {
    shortDesc = getRoleShort(userRoles);
  }
  return shortDesc;
};
</script>
<script lang="ts">
export default {
  data() {
    return { buildDate: __BUILD_DATE__ };
  },
};
</script>

<template>
  <div>
    <ScuModalDialogue
      :is-modal="true"
      :is-visible="isModalDialogOpen"
      data-testid="app-info-modal-dialogue"
      @scutoggle="isModalDialogOpen = $event.detail"
    >
      <div slot="body">
        <ScuHeadline class="headline" element="h2">{{ t('pages.system.systemInfo.titleGateway') }}</ScuHeadline>
        <p>
          {{
            getInfoText({
              componentName: t('pages.system.systemInfo.frontend'),
              componentVersion: config.app.version,
              componentBuildDate: buildDate,
            } satisfies VersionInfoModel)
          }}
        </p>
        <p v-for="(versionInfo, index) in appInfo.gatewayVersionInfos" :key="index">
          {{ getInfoText(versionInfo) }}
        </p>

        <ScuHeadline class="headline" element="h2">{{ t('pages.system.systemInfo.titleDatapool') }}</ScuHeadline>
        <p v-for="(versionInfo, index) in appInfo.datapoolVersionInfos" :key="index">
          {{ getInfoText(versionInfo) }}
        </p>

        <br />
        <div>
          <ScuButton
            slot="custom-buttons"
            variant="outline"
            color="secondary"
            :text="t('button.close')"
            data-testid="close-app-info-dialogue-button"
            @scuclick="openModalInfo(false)"
          />
        </div>
      </div>
    </ScuModalDialogue>

    <ScuTopBar
      :app-icon-src="logoUrl"
      :app-name="config.app.name"
      :nav-items="navItems"
      :more-label="t('global.more')"
      show-back-button
      @scubackbuttonclick="emit('backButtonClick')"
      @scuhomenavigationclick="emit('navigate', '/')"
      @scunavitemclick="$event.detail.href && emit('navigate', $event.detail.href)"
    >
      <ScuTopBarContextDropdown
        v-if="determineFunctionVisibility(PermissionFunction.System, authStore)"
        slot="context-area"
        :label="t('pages.system.pageName')"
        data-testid="system-dropdown"
        :items-visible="18"
      >
        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.Rollen, authStore)"
          :href="RolesPaths[0]"
          mode="list"
        >
          <ScuIcon :src="user_group" />{{ t('pages.system.roles.name') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.Berechtigungen, authStore)"
          :href="PermissionsPaths[0]"
          mode="list"
        >
          <ScuIcon :src="user_check" />{{ t('pages.system.permissions.name') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.Berichte, authStore)"
          :href="ReportsPaths[0]"
          mode="list"
        >
          <ScuIcon :src="newspaper" />{{ t('pages.system.reports.name') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.NewsVerwaltung, authStore)"
          :href="NewsAdministrationPaths[0]"
          mode="list"
        >
          <ScuIcon :src="bell" />{{ t('pages.system.newsAdministration.name') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.TechnischeParameter, authStore)"
          :href="TechicalParametersPaths[0]"
          mode="list"
        >
          <ScuIcon :src="key_settings" />{{ t('pages.system.parameters.technicalName') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.FachlicheParameter, authStore)"
          :href="BusinessParametersPaths[0]"
          mode="list"
        >
          <ScuIcon :src="computer_settings" />{{ t('pages.system.parameters.businessName') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.ManuelleAufrufe, authStore)"
          :href="ManualCallsPaths[0]"
          mode="list"
        >
          <ScuIcon :src="send" />{{ t('pages.system.manualCalls.name') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.LogTabelle, authStore)"
          :href="LoggingsPaths[0]"
          mode="list"
        >
          <ScuIcon :src="setting_search" />{{ t('pages.system.loggings.name') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.EdiError, authStore)"
          :href="EdiErrorPath"
          mode="list"
        >
          <ScuIcon :src="triangle_warning" />{{ t('pages.system.ediErrors.name') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.FileTransferError, authStore)"
          :href="FileTransferErrorPath"
          mode="list"
        >
          <ScuIcon :src="file_x" />{{ t('pages.system.fileTransferErrors.name') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.SchedulerTabelle, authStore)"
          :href="SchedulerPaths[0]"
          mode="list"
        >
          <ScuIcon :src="calendar_clock" />{{ t('pages.system.scheduler.name') }}
        </ScuLink>

        <ScuLink
          v-if="determineFunctionVisibility(PermissionFunction.DispoImport, authStore) && currentStage !== 'PROD'"
          :href="DispoImportPath"
          mode="list"
        >
          <ScuIcon :src="upload" />{{ t('pages.system.dispoImport.name') }}
        </ScuLink>

        <ScuLink mode="list" @scuclick="openModalInfo(true)"
          ><ScuIcon :src="circle_attention" />{{ t('pages.system.systemInfo.name') }}
        </ScuLink>
      </ScuTopBarContextDropdown>

      <ScuChip v-show="stage != ''" slot="context-area" :label="stage" class="stage-chip" />

      <ScuTopBarContextDropdown
        slot="context-area"
        :flag="localeLabel === 'DE' ? DE : GB"
        data-testid="language-dropdown"
      >
        <ScuLink
          v-for="(languageName, languageLocale) in config.i18n.locales"
          :key="languageLocale"
          mode="list"
          :active="languageLocale === props.locale"
          @scuclick="emit('update:locale', languageLocale)"
        >
          {{ languageName }}
        </ScuLink>
      </ScuTopBarContextDropdown>

      <ScuTopBarUser
        slot="user-options"
        class="user"
        data-testid="top-bar-user"
        :icon="profile"
        :user-name="props.username ?? t('menu.login')"
        :user-description="getUserRolesShortDescription(authStore.userRoles)"
        :hide-user-info="props.username == null"
        :app-version="t('menu.version', { version: config.app.version })"
      >
        <ScuAvatar slot="avatar" />

        <ScuLink v-if="props.username" mode="list" color="var(--sit-color-red)" @scuclick="emit('logout')">
          <ScuIcon :src="logout" />
          {{ t('menu.logout') }}
        </ScuLink>

        <ScuLink v-else mode="list" @scuclick="handleLoginButtonClick">
          <ScuIcon :src="login" />
          {{ t('menu.login') }}
        </ScuLink>
      </ScuTopBarUser>
    </ScuTopBar>
  </div>
</template>

<style language="scss">
:root {
  --top-bar-height: 60px;
}
.stage-chip {
  --background-color: var(--orange-4);
}
.user {
  white-space: pre-line;
}
</style>
